import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import { supabase } from '../../../lib/supabase';
import { useAuth } from '../../../contexts/AuthContext';

const CallToAction = ({ title, description, cta_text, cta_link }) => {
  const authContext = useAuth();
  const user = authContext?.user || null;
  const signupDestination = user ? "/model-catalog" : "/signup";
  const [metrics, setMetrics] = useState({ products: '50K+', parts: '15K+', possibilities: '2M+' });

  useEffect(() => {
    const fetchMetrics = async () => {
      try {
        const { count: productCount } = await supabase.from('products').select('*', { count: 'exact', head: true });
        const { data: productsData } = await supabase.from('products').select('component_count');
        const totalParts = productsData?.reduce((sum, item) => sum + (item.component_count || 0), 0) || 0;

        const productNum = productCount !== null ? productCount : 50;
        const partsNum = totalParts > 0 ? totalParts : 15000;
        const totalPossibilities = productNum * partsNum;
        
        let formattedPossibilities = '2M+';
        if (totalPossibilities >= 1000000) {
          formattedPossibilities = `${(totalPossibilities / 1000000).toFixed(1)}M+`;
        } else if (totalPossibilities >= 1000) {
          formattedPossibilities = `${(totalPossibilities / 1000).toFixed(0)}K+`;
        } else if (totalPossibilities > 0) {
          formattedPossibilities = `${totalPossibilities}+`;
        }

        setMetrics({
          products: productCount !== null ? `${productCount.toLocaleString()}+` : '50K+',
          parts: totalParts > 0 ? `${totalParts.toLocaleString()}+` : '15K+',
          possibilities: formattedPossibilities
        });
      } catch (err) {
        console.error('CTA metrics fetch error:', err);
      }
    };
    fetchMetrics();
  }, []);

  const features = [
    {
      icon: 'Zap',
      title: 'Instant Access',
      description: 'Access to the Parts you Need, Right Now.  No Waiting, Just Creating'
    },
    {
      icon: 'Shield',
      title: 'Quality Guaranteed',
      description: 'Premium Parts, No Guesswork.'
    },
    {
      icon: 'Users',
      title: 'Community Support',
      description: 'Expert Help and Friendly Vibes'
    },
    {
      icon: 'Infinity',
      title: 'Unlimited Usage',
      description: 'Build More, Save More, Restrict Less'
    }
  ];

  const stats = [
    { number: metrics.products, label: 'Premium Products' },
    { number: metrics.parts, label: 'Standardized Parts' },
    { number: metrics.possibilities, label: 'Dynamic Possibilities' },
    { number: '99.9%', label: 'Uptime Guarantee' }
  ];

  return (
    <section className="py-20 bg-gradient-to-br from-primary via-primary/90 to-accent relative overflow-hidden">
      {/* Background Pattern */}
      <div className="absolute inset-0 opacity-10">
        <div className="absolute inset-0 bg-[url('data:image/svg+xml,%3Csvg%20width%3D%22100%22%20height%3D%22100%22%20viewBox%3D%220%200%20100%20100%22%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%3E%3Cg%20fill%3D%22white%22%20fill-opacity%3D%220.1%22%3E%3Cpolygon%20points%3D%2250%200%2060%2040%20100%2050%2060%2060%2050%20100%2040%2060%200%2050%2040%2040%22%2F%3E%3C%2Fg%3E%3C%2Fsvg%3E')] bg-repeat"></div>
      </div>
      {/* Floating Elements */}
      <div className="absolute top-20 left-10 w-20 h-20 bg-white/10 rounded-full animate-float"></div>
      <div className="absolute bottom-20 right-10 w-32 h-32 bg-white/5 rounded-full animate-pulse-soft"></div>
      <div className="absolute top-1/2 left-1/4 w-16 h-16 bg-accent/20 rounded-full animate-float" style={{ animationDelay: '1s' }}></div>
      <div className="container mx-auto px-6 relative z-10">
        {/* Main CTA Content */}
        <div className="text-center mb-16">
          <div className="inline-flex items-center space-x-2 bg-white/20 backdrop-blur-sm text-white px-4 py-2 rounded-full text-sm font-medium mb-6">
            <Icon name="Rocket" size={16} />
            <span>Start Creating Today</span>
          </div>
          
          <h2 className="text-4xl lg:text-6xl font-bold text-white mb-6 leading-tight">
            {title || 'Ready to Transform'}
            <br />
            <span className="text-accent">Your Vision?</span>
          </h2>
          
          <p className="text-xl text-white/90 max-w-3xl mx-auto mb-8 leading-relaxed">
            {description || `Join thousands of creators who trust OpenGadget for their 3D catalog and parts needs.
            Start your journey with our premium collection and bring your ideas to life.`}
          </p>

          {/* CTA Buttons */}
          <div className="flex flex-col sm:flex-row gap-4 justify-center mb-12">
            <Link to={cta_link || "/model-catalog"}>
              <Button 
                variant="secondary" 
                size="xl" 
                iconName="Search" 
                iconPosition="left"
                className="w-full sm:w-auto bg-white text-primary hover:bg-white/90 shadow-floating"
              >
                {cta_text || `Browse ${metrics.products} Products`}
              </Button>
            </Link>
            
            <Link to={signupDestination}>
              <Button 
                variant="outline" 
                size="xl" 
                iconName="UserPlus" 
                iconPosition="left"
                className="w-full sm:w-auto border-white/30 text-white hover:bg-white/10 backdrop-blur-sm"
              >
                Create Free Account
              </Button>
            </Link>
          </div>

          {/* Trust Indicators */}
          <div className="flex flex-wrap justify-center items-center gap-8 text-white/80 text-sm">
            <div className="flex items-center space-x-2">
              <Icon name="Shield" size={16} />
              <span>SSL Secured</span>
            </div>
            <div className="flex items-center space-x-2">
              <Icon name="CreditCard" size={16} />
              <span>Secure Payments</span>
            </div>
            <div className="flex items-center space-x-2">
              <Icon name="Download" size={16} />
              <span>Instant Downloads</span>
            </div>
            <div className="flex items-center space-x-2">
              <Icon name="Headphones" size={16} />
              <span>24/7 Support</span>
            </div>
          </div>
        </div>

        {/* Stats Section */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-8 mb-16">
          {stats?.map((stat, index) => (
            <div key={index} className="text-center">
              <div className="text-3xl lg:text-4xl font-bold text-white mb-2">
                {stat?.number}
              </div>
              <div className="text-white/80 text-sm lg:text-base">
                {stat?.label}
              </div>
            </div>
          ))}
        </div>

        {/* Features Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
          {features?.map((feature, index) => (
            <div
              key={index}
              className="bg-white/10 backdrop-blur-sm rounded-xl p-6 text-center hover:bg-white/15 transition-all duration-300 transform hover:-translate-y-1"
            >
              <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center mx-auto mb-4">
                <Icon name={feature?.icon} size={24} color="white" />
              </div>
              <h3 className="font-semibold text-white mb-2">
                {feature?.title}
              </h3>
              <p className="text-white/80 text-sm leading-relaxed">
                {feature?.description}
              </p>
            </div>
          ))}
        </div>

        {/* Bottom CTA */}
        <div className="text-center mt-16 pt-12 border-t border-white/20">
          <p className="text-white/90 mb-6">
            Ready to get started? Create your free account and explore our collection.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to={signupDestination}>
              <Button 
                variant="secondary" 
                size="lg" 
                iconName="ArrowRight" 
                iconPosition="right"
                className="w-full sm:w-auto bg-white text-primary hover:bg-white/90"
              >
                Get Started Free
              </Button>
            </Link>
            <Link to="/demo">
              <Button 
                variant="ghost" 
                size="lg" 
                iconName="Play" 
                iconPosition="left"
                className="w-full sm:w-auto text-white hover:bg-white/10"
              >
                Watch Demo
              </Button>
            </Link>
          </div>
        </div>
      </div>
    </section>
  );
};

export default CallToAction;